import {
  memo,
  useState,
  useCallback,
  useEffect,
  ChangeEvent,
  SyntheticEvent,
  useMemo,
} from "react";
import {
  Modal,
  Form,
  FormField,
  Button,
  Input,
  Header,
  FormGroup,
  Label,
  Dropdown,
  DropdownProps,
  TextArea,
  Icon,
  Dimmer,
  Loader,
} from "semantic-ui-react";

// Components
import UserProfileImage from "../IsHealth/REG/UserProfileImage";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { DateTextBox } from "../common";

// Types
import { PickedState, CreateCustomerParams, SetProp, BTN_ACTS } from "./types/MeBillingType";

// Utils
import moment from "moment";
import { calculateAge, formatBirthdateAD } from "./CustomerInfoHeader";
import * as CAgent from "react-lib/apps/common/CAgent";
import { adToBe } from "react-lib/utils/dateUtils";

export type CitizenType = "T" | "F" | "N";

type ModMeBillingCustomerProps = {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateCustomerParams) => void;
  setProp: SetProp;
  // data
  initialData: CreateCustomerParams | null;
  isPatientQueue?: boolean;
  masterData: any;
  languageUX: "en" | "th";
} & Pick<PickedState, "buttonLoadCheck" | "masterOptions">;

const INITIAL_FORM: CreateCustomerParams = {
  citizen_type: "T",

  // -- iden
  citizen_no: "", // citizen_type === "T"
  passport_no: "", // citizen_type === "F"

  // thai name
  pre_name_th: 0,
  first_name_th: "",
  last_name_th: "",

  // eng name
  pre_name_en: 0,
  first_name_en: "",
  middle_name_en: "",
  last_name_en: "",

  birthdate: "",
  gender: "",

  // -- address
  present_address: {
    no: "",
    name: "",
    type: 0,
    town: "",
    street: "",
    road: "",
    province: 0,
    district: 0,
    city: 0,
    zipcode: "",
    tel_mobile: "",
    email: "",
  },

  // extends
  email: "",
  line_id: "",
  profile_image: null,
};

const IMAGES = {
  profile: "/static/images/person-placeholder.png",
};

// Options for dropdowns
// (Ref: CardPatientInfo.tsx)
// todo --- Issue [#83180] ให้มี 3 types คือ "T" | "F" | "N"
const CITIZEN_TYPE_OPTIONS = [
  { key: "T", value: "T", text: "หมายเลขบัตรประชาชน" },
  { key: "F", value: "F", text: "พาสปอร์ต" },
  { key: "N", value: "N", text: "ไม่มีเอกสารระบุตัวตน" },
];

type RequiredFieldsConfig = {
  citizen_no: boolean;
  passport_no: boolean;
  pre_name_th: boolean;
  first_name_th: boolean;
  last_name_th: boolean;
  pre_name_en: boolean;
  first_name_en: boolean;
  last_name_en: boolean;
  gender: boolean;
  birthdate: boolean;
  tel_mobile: boolean;
  relative_address_owner_name: boolean;
  relative_address_tel_mobile: boolean;
  type: boolean;
  province: boolean;
  district: boolean;
  city: boolean;
};

const REQUIRED_FIELDS_BY_CITIZEN_TYPE: Record<CitizenType, RequiredFieldsConfig> = {
  T: {
    citizen_no: true,
    passport_no: false,
    pre_name_th: true,
    first_name_th: true,
    last_name_th: true,
    pre_name_en: false,
    first_name_en: false,
    last_name_en: false,
    gender: true,
    birthdate: true,
    tel_mobile: true,
    relative_address_owner_name: true,
    relative_address_tel_mobile: true,
    type: true,
    province: true,
    district: true,
    city: true,
  },
  F: {
    citizen_no: false,
    passport_no: true,
    pre_name_th: false,
    first_name_th: false,
    last_name_th: false,
    pre_name_en: true,
    first_name_en: true,
    last_name_en: true,
    gender: true,
    birthdate: true,
    tel_mobile: true,
    relative_address_owner_name: true,
    relative_address_tel_mobile: true,
    type: true,
    province: true,
    district: true,
    city: true,
  },
  N: {
    citizen_no: false,
    passport_no: false,
    pre_name_th: true,
    first_name_th: true,
    last_name_th: true,
    pre_name_en: false,
    first_name_en: false,
    last_name_en: false,
    gender: true,
    birthdate: true,
    tel_mobile: true,
    relative_address_owner_name: true,
    relative_address_tel_mobile: true,
    type: true,
    province: true,
    district: true,
    city: true,
  },
};

// todo --- ย้ายไป dateUtils
/**
 * แปลง BE Date String (dd/mm/yyyy) เป็น AD Date String (yyyy-mm-dd)
 * @param beDate - "13/01/2569"
 * @returns "2026-01-13"
 */
const convertBEtoAD = (beDate: string): string => {
  if (!beDate) return "";

  const parts = beDate.split("/");
  if (parts.length !== 3) return "";

  const [day, month, yearBE] = parts;
  const yearAD = parseInt(yearBE, 10) - 543;

  const formatted = `${yearAD}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  const parsed = moment(formatted, "YYYY-MM-DD", true);

  return parsed.isValid() ? formatted : "";
};

/**
 * แปลง AD Date String (yyyy-mm-dd) เป็น BE Date String (dd/mm/yyyy)
 * @param adDate - "2026-01-13"
 * @returns "13/01/2569"
 */
const convertADtoBE = (adDate: string): string => {
  if (!adDate) return "";

  const parsed = moment(adDate, "YYYY-MM-DD", true);
  if (!parsed.isValid()) return "";

  const day = parsed.format("DD");
  const month = parsed.format("MM");
  const yearBE = parsed.year() + 543;

  return `${day}/${month}/${yearBE}`;
};

const ModMeBillingCustomer = (props: ModMeBillingCustomerProps) => {
  const { open, onClose, onSave, initialData, masterData, masterOptions, buttonLoadCheck } = props;

  const isEditMode = !!initialData?.id;

  const [formData, setFormData] = useState<CreateCustomerParams>(INITIAL_FORM);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);
  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateCustomerParams | string, string>>
  >({});

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData(initialData);
      } else {
        setFormData(INITIAL_FORM);
      }

      setErrors({});
    }
  }, [open, initialData]);

  // const birthdateBE = useMemo(() => {
  //   return convertADtoBE(formData.birthdate);
  // }, [formData.birthdate]);

  const requiredFields = useMemo((): RequiredFieldsConfig => {
    const citizenType = formData.citizen_type || "T";
    return (
      REQUIRED_FIELDS_BY_CITIZEN_TYPE[citizenType as CitizenType] ||
      REQUIRED_FIELDS_BY_CITIZEN_TYPE.T
    );
  }, [formData.citizen_type]);

  const handleChangeData = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name } = e.target;

      const filteredValue = sanitizeInput(e.target.value);

      setFormData((prev) => ({
        ...prev,
        [name]: filteredValue,
      }));

      if (errors[name as keyof CreateCustomerParams]) {
        setErrors((prev) => ({
          ...prev,
          [name]: undefined,
        }));
      }
    },
    [errors],
  );

  const handleChangeAddressData = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        present_address: {
          ...prev.present_address,
          [name]: value,
        },
      }));

      if (errors[`present_address.${name}`]) {
        setErrors((prev) => ({ ...prev, [`present_address.${name}`]: undefined }));
      }
    },
    [errors],
  );

  const handleChangeRelativeAddressData = useCallback(
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
      const { name, value } = e.target;

      setFormData((prev) => ({
        ...prev,
        relative_address: {
          ...prev.relative_address,
          [name]: value,
        },
      }));

      if (errors[`relative_address_${name}`]) {
        setErrors((prev) => ({
          ...prev,
          [`relative_address_${name}`]: undefined,
        }));
      }
    },
    [errors],
  );

  const handleChangeRelative = useCallback(
    (_: React.SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const { name, value } = data;

      setFormData((prev) => ({
        ...prev,
        relative_address: {
          ...prev.relative_address,
          [name as string]: value,
        },
      }));

      if (errors[`relative_address_${name}`]) {
        setErrors((prev) => ({
          ...prev,
          [`relative_address_${name}`]: undefined,
        }));
      }
    },
    [errors],
  );

  const handleChangeEmail = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      setFormData((prev) => ({
        ...prev,
        email: value,
        present_address: {
          ...prev.present_address,
          email: value,
        },
      }));

      if (errors.email) {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
    },
    [errors],
  );

  const handleChangeDropdown = useCallback(
    (e: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const { name, value } = data;

      setFormData((prev) => {
        const newData = { ...prev, [name as string]: value };

        if (name === "citizen_type") {
          newData.citizen_no = "";
          newData.passport_no = "";
        }

        return newData;
      });

      if (errors[name as keyof CreateCustomerParams]) {
        setErrors((prev) => ({ ...prev, [name as string]: undefined }));
      }
    },
    [errors],
  );

  const handleChangeAddressDropdown = useCallback(
    (e: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
      const { name, value } = data;

      setFormData((prev) => {
        const newAddress = { ...prev.present_address, [name as string]: value };

        if (name === "province") {
          newAddress.district = 0;
          newAddress.city = 0;
          newAddress.zipcode = "";
        }

        if (name === "district") {
          newAddress.city = 0;
          newAddress.zipcode = "";
        }

        if (name === "city") {
          const foundCity = (masterData?.city || []).find((item: any) => item.id === value);

          newAddress.zipcode = foundCity?.zipcode || "";
        }

        return {
          ...prev,
          present_address: newAddress,
        };
      });

      if (errors[`present_address.${name}`]) {
        setErrors((prev) => ({ ...prev, [`present_address.${name}`]: undefined }));
      }
    },
    [errors, masterData?.city],
  );

  const handleChangeDate = useCallback(
    (date: string) => {
      // const adDate = convertBEtoAD(date);

      setFormData((prev) => ({ ...prev, birthdate: date }));

      if (errors.birthdate) {
        setErrors((prev) => ({ ...prev, birthdate: undefined }));
      }
    },
    [errors],
  );

  const handleChangeImage = useCallback((file: File, fileUrl: string) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result as string;

      setFormData((prev) => ({
        ...prev,
        profile_image: {
          image: base64String,
          image_type: "P",
          ...(prev.id ? { patient: prev.id } : {}),
        },
      }));
    };

    reader.readAsDataURL(file);
  }, []);

  const handleClearImage = useCallback(() => {
    setFormData((prev) => ({ ...prev, profile_image: null }));
  }, []);

  const [smartCardLoading, setSmartCardLoading] = useState(false);
  const [smartCardError, setSmartCardError] = useState<string | null>(null);

  const handleSmartCard = useCallback(() => {
    setSmartCardError(null);

    CAgent.smartcard(
      (result: any) => {
        setSmartCardLoading(true);

        const prenameThOptions = masterOptions?.prenameTh || [];
        const prenameEnOptions = masterOptions?.prenameEn || [];
        const provinceOptions = masterOptions?.province || [];

        const prenameTh =
          prenameThOptions.find((item: any) => item.text === result.th_prefix)?.value || 0;
        const prenameEn =
          prenameEnOptions.find((item: any) => item.text === result.en_prefix)?.value || 0;

        const province =
          provinceOptions.find(
            (item: any) => item.text === (result.addr_province || "").replace(/^จังหวัด/, ""),
          )?.value || 0;

        const districtOptions = (masterData?.district || []).filter(
          (item: any) => item.area === province,
        );
        const district =
          districtOptions.find(
            (item: any) => item.name === (result.addr_amphur || "").replace(/^อำเภอ/, ""),
          )?.id || 0;

        const cityList = (masterData?.city || []).filter((item: any) => item.area === district);
        const cityItem = cityList.find(
          (item: any) => item.name === (result.addr_tambol || "").replace(/^(แขวง|ตำบล)\s*/, ""),
        );
        const city = cityItem?.id || 0;
        const zipcode = cityItem?.zipcode || "";

        const gender = result.sex === "1" ? "M" : "F";

        setFormData((prev) => ({
          ...prev,
          citizen_type: "T" as CitizenType,
          citizen_no: result.citizen_id || prev.citizen_no,
          pre_name_th: prenameTh || prev.pre_name_th,
          first_name_th: result.th_firstname || prev.first_name_th,
          last_name_th: result.th_lastname || prev.last_name_th,
          pre_name_en: prenameEn || prev.pre_name_en,
          first_name_en: result.en_firstname || prev.first_name_en,
          last_name_en: result.en_lastname || prev.last_name_en,
          gender,
          birthdate: adToBe(result.birth_date) || prev.birthdate,
          present_address: {
            ...prev.present_address,
            no: result.addr_house_no || "",
            town: result.addr_village_no || "",
            street: result.addr_lane || "",
            road: result.addr_road || "",
            province,
            district,
            city,
            zipcode,
          },
          ...(result.picture_base64
            ? {
                profile_image: {
                  image: result.picture_base64,
                  image_type: "P",
                  ...(prev.id ? { patient: prev.id } : {}),
                },
              }
            : {}),
        }));

        setSmartCardLoading(false);
      },
      (error: any) => {
        setSmartCardError(typeof error === "string" ? error : "ไม่สามารถอ่านข้อมูล Smart Card ได้");
        setSmartCardLoading(false);
      },
    );
  }, [masterOptions, masterData]);

  const validate = useCallback((): boolean => {
    const newErrors: Partial<
      Record<keyof CreateCustomerParams | "tel_mobile" | "province" | "district" | "city", string>
    > = {};
    const fields = requiredFields;

    if (!formData.citizen_type) {
      newErrors.citizen_type = " กรุณาเลือกประเภทเอกสารระบุตัวตน";
    }

    if (fields.citizen_no && !formData?.citizen_no?.trim()) {
      newErrors.citizen_no = "กรุณาระบุเลขที่บัตรประชาชน";
    }

    if (fields.passport_no && !formData.passport_no?.trim()) {
      newErrors.passport_no = "กรุณาระบุเลขที่พาสปอร์ต";
    }

    if (fields.pre_name_th && !formData.pre_name_th) {
      newErrors.pre_name_th = "กรุณาเลือกคำนำหน้าชื่อ";
    }

    if (fields.first_name_th && !formData?.first_name_th?.trim()) {
      newErrors.first_name_th = "กรุณากรอกชื่อ";
    }

    if (fields.last_name_th && !formData?.last_name_th?.trim()) {
      newErrors.last_name_th = "กรุณากรอกนามสกุล";
    }

    if (fields.pre_name_en && !formData.pre_name_en) {
      newErrors.pre_name_en = "กรุณาเลือก Prefix";
    }

    if (fields.first_name_en && !formData.first_name_en?.trim()) {
      newErrors.first_name_en = "กรุณากรอก Firstname";
    }

    if (fields.last_name_en && !formData.last_name_en?.trim()) {
      newErrors.last_name_en = "กรุณากรอก Lastname";
    }

    if (fields.gender && !formData.gender) {
      newErrors.gender = "กรุณาระบุเพศ";
    }

    if (fields.birthdate && !formData.birthdate?.trim()) {
      newErrors.birthdate = "กรุณากรอกวันเดือนปีเกิด";
    }

    if (fields.tel_mobile && !formData.present_address?.tel_mobile?.trim()) {
      newErrors.tel_mobile = "กรุณากรอกเบอร์โทรศัพท์";
    }

    if (fields.relative_address_owner_name && !formData.relative_address?.owner_name?.trim()) {
      newErrors.relative_address_owner_name = "กรุณาระบุชื่อ - นามสกุลผู้ติดต่อฉุกเฉิน";
    }

    if (fields.relative_address_tel_mobile && !formData.relative_address?.tel_mobile?.trim()) {
      newErrors.relative_address_tel_mobile = "กรุณาระบุชื่อ - นามสกุลผู้ติดต่อฉุกเฉิน";
    }

    if (fields.type && !formData.present_address?.type) {
      newErrors.type = "กรุณาระบุประเภทที่พักอาศัย";
    }

    if (fields.province && !formData.present_address?.province) {
      newErrors.province = "กรุณาระบุประเภทที่พักอาศัย";
    }

    if (fields.district && !formData.present_address?.district) {
      newErrors.district = "กรุณาระบุประเภทที่พักอาศัย";
    }

    if (fields.city && !formData.present_address?.city) {
      newErrors.city = "กรุณาระบุประเภทที่พักอาศัย";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, requiredFields]);

  const handleSubmit = useCallback(() => {
    if (validate()) {
      onSave(formData);
    }
  }, [validate, formData, onSave]);

  const handleClose = useCallback(() => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setSmartCardError(null);
    onClose();
  }, [onClose]);

  const modalTitle = isEditMode ? "แก้ไขข้อมูลผู้รับบริการ" : "ข้อมูลผู้รับบริการ";
  const submitButtonText = isEditMode ? "Update" : "Save";

  const requiredMark = <span style={{ color: "red" }}>*</span>;

  const renderRequired = useCallback(
    (field: keyof RequiredFieldsConfig) => {
      return requiredFields[field] ? requiredMark : null;
    },
    [requiredFields],
  );

  const labelStyle = { minWidth: "120px", fontSize: "14px" };
  const labelCompStyle = {
    width: "100%",
    height: "2.67rem",
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    fontWeight: "normal",
    fontSize: "14px",
  };

  // --- Options
  const districtOptions = useMemo(() => {
    return (
      (masterOptions?.district || []).filter(
        (item: any) => item.relate === formData.present_address?.province,
      ) || []
    );
  }, [masterOptions?.district, formData]);

  const cityOptions = useMemo(() => {
    return (
      (masterOptions?.city || []).filter(
        (item: any) => item.relate === formData.present_address?.district,
      ) || []
    );
  }, [masterOptions?.city, formData]);

  const profileImageUrl = useMemo(() => {
    if (typeof formData.profile_image === "string") {
      return formData.profile_image;
    }

    return formData.profile_image?.image || IMAGES.profile;
  }, [formData.profile_image]);

  const showCitizenNo = formData.citizen_type === "T";
  const showPassportNo = formData.citizen_type === "F";

  return (
    <Modal open={open} onClose={handleClose} size="fullscreen">
      <Dimmer active={initialData === null && props.isPatientQueue} inverted>
        <Loader inverted>Loading</Loader>
      </Dimmer>
      <div style={{ display: "flex", padding: "1.5rem 0" }}>
        <div
          style={{
            padding: "0 1.5rem",
            borderRight: "solid 1px #e0e0e0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "275px",
          }}
        >
          <UserProfileImage
            src={profileImageUrl}
            onChange={handleChangeImage}
            onClear={handleClearImage}
          />

          <Button
            basic
            loading={smartCardLoading}
            disabled={smartCardLoading}
            onClick={handleSmartCard}
            size="small"
            style={{
              marginTop: "1rem",
              borderRadius: "8px",
              border: "1px solid var(--primary-theme-color)",
            }}
          >
            <Icon name="id card" style={{ color: "var(--primary-theme-color)" }} />
            <label style={{ color: "var(--primary-theme-color)" }}>Smart Card</label>
          </Button>

          {smartCardError && (
            <div
              style={{
                color: "red",
                fontSize: "12px",
                marginTop: "1rem",
                textAlign: "center",
                padding: "0 1rem",
              }}
            >
              {smartCardError}
            </div>
          )}
        </div>
        <div style={{ padding: "0 1.5rem", flex: 1 }}>
          <Form>
            <FormGroup inline>
              <FormField style={{ flex: 1 }}>
                <Header style={{ color: "var(--primary-theme-color)", margin: "0" }} as="h2">
                  {modalTitle}
                </Header>
              </FormField>
              <FormField>
                <label style={labelStyle}>หมายเลขผู้รับบริการ</label>
              </FormField>
              <FormField width={3}>
                <Label basic style={labelCompStyle} content={formData.display_hn || ""} />
              </FormField>
              <FormField>
                <label style={labelStyle}>ไอดีผู้ใช้งาน</label>
              </FormField>
              <FormField width={3}>
                <Label basic style={labelCompStyle} content={formData.id || ""} />
              </FormField>
            </FormGroup>
          </Form>

          <Form>
            <FormGroup>
              <FormField width={3} error={!!errors.citizen_type}>
                <label style={labelStyle}>ประเภทเอกสารระบุตัวตน{requiredMark}</label>
                <Dropdown
                  name="citizen_type"
                  selection
                  fluid
                  options={CITIZEN_TYPE_OPTIONS}
                  value={formData.citizen_type}
                  onChange={handleChangeDropdown}
                  error={!!errors.citizen_type}
                />
              </FormField>
              {showCitizenNo && (
                <FormField width={4} error={!!errors.citizen_no}>
                  <label style={labelStyle}>
                    เลขที่เอกสารระบุตัวตน{renderRequired("citizen_no")}
                  </label>
                  <Input
                    name="citizen_no"
                    placeholder="กรุณาระบุเลขที่บัตรประชาชน"
                    value={formData.citizen_no || ""}
                    onChange={handleChangeData}
                    maxLength={13}
                    error={!!errors.citizen_no}
                  />
                </FormField>
              )}

              {showPassportNo && (
                <FormField width={4} error={!!errors.passport_no}>
                  <label style={labelStyle}>
                    เลขที่เอกสารระบุตัวตน{renderRequired("passport_no")}
                  </label>
                  <Input
                    name="passport_no"
                    placeholder="กรุณาระบุเลขที่พาสปอร์ต"
                    value={formData.passport_no || ""}
                    onChange={handleChangeData}
                    error={!!errors.passport_no}
                  />
                </FormField>
              )}
            </FormGroup>

            <FormGroup>
              <FormField width={4} error={!!errors.pre_name_th}>
                <label style={labelStyle}>คำนำหน้า{renderRequired("pre_name_th")}</label>
                <Dropdown
                  name="pre_name_th"
                  placeholder="ระบุคำนำหน้า"
                  selection
                  fluid
                  options={masterOptions?.prenameTh || []}
                  value={formData.pre_name_th || 0}
                  onChange={handleChangeDropdown}
                  error={!!errors.pre_name_th}
                />
              </FormField>
              <FormField width={6} error={!!errors.first_name_th}>
                <label style={labelStyle}>ชื่อ{renderRequired("first_name_th")}</label>
                <Input
                  name="first_name_th"
                  placeholder="กรุณาระบุชื่อ"
                  value={formData.first_name_th}
                  onChange={handleChangeData}
                  error={!!errors.first_name_th}
                />
              </FormField>
              <FormField width={6} error={!!errors.last_name_th}>
                <label style={labelStyle}>นามสกุล{renderRequired("last_name_th")}</label>
                <Input
                  name="last_name_th"
                  placeholder="กรุณาระบุนามสกุล"
                  value={formData.last_name_th}
                  onChange={handleChangeData}
                  error={!!errors.last_name_th}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={4} error={!!errors.pre_name_en}>
                <label style={labelStyle}>Prefix{renderRequired("pre_name_en")}</label>
                <Dropdown
                  name="pre_name_en"
                  placeholder="ระบุคำนำหน้า EN"
                  selection
                  fluid
                  options={masterOptions?.prenameEn || []}
                  value={formData.pre_name_en || 0}
                  onChange={handleChangeDropdown}
                  error={!!errors.pre_name_en}
                />
              </FormField>
              <FormField width={4} error={!!errors.first_name_en}>
                <label style={labelStyle}>Firstname{renderRequired("first_name_en")}</label>
                <Input
                  name="first_name_en"
                  placeholder="กรุณาระบุชื่อภาษาอังกฤษ"
                  value={formData.first_name_en}
                  onChange={handleChangeData}
                  error={!!errors.first_name_en}
                />
              </FormField>
              <FormField width={4}>
                <label style={labelStyle}>Middle name</label>
                <Input
                  name="middle_name_en"
                  placeholder="Specify Middle Name"
                  value={formData.middle_name_en}
                  onChange={handleChangeData}
                />
              </FormField>
              <FormField width={4} error={!!errors.last_name_en}>
                <label style={labelStyle}>Lastname{renderRequired("last_name_en")}</label>
                <Input
                  name="last_name_en"
                  placeholder="กรุณาระบุนามสกุลภาษาอังกฤษ"
                  value={formData.last_name_en}
                  onChange={handleChangeData}
                  error={!!errors.last_name_en}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={4} error={!!errors.gender}>
                <label style={labelStyle}>เพศ{renderRequired("gender")}</label>
                <Dropdown
                  name="gender"
                  placeholder="ระบุตัวเลือกเพศ"
                  selection
                  fluid
                  options={masterOptions?.gender || []}
                  value={formData.gender || ""}
                  onChange={handleChangeDropdown}
                  error={!!errors.gender}
                />
              </FormField>
              <FormField width={6} error={!!errors.birthdate}>
                <label style={labelStyle}>วันเกิด{renderRequired("birthdate")}</label>
                <DateTextBox
                  placeholder="วว/ดด/ปปปป"
                  value={formData.birthdate}
                  onChange={handleChangeDate}
                  error={!!errors.birthdate}
                />
              </FormField>
              <FormField width={6}>
                <label style={labelStyle}>อายุ</label>
                <Label
                  basic
                  style={labelCompStyle}
                  content={calculateAge(formData.birthdate) || " วัน เดือน ปี"}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={6} error={!!errors.tel_mobile}>
                <label style={labelStyle}>เบอร์โทร{renderRequired("tel_mobile")}</label>
                <Input
                  name="tel_mobile"
                  placeholder="กรุณาระบุหมายเลขโทรศัพท์"
                  value={formData.present_address?.tel_mobile || ""}
                  onChange={handleChangeAddressData}
                  error={!!errors.tel_mobile}
                />
              </FormField>
              <FormField width={6} error={!!errors.email}>
                <label style={labelStyle}>Email</label>
                <Input
                  name="email"
                  type="email"
                  placeholder="example@mail.com"
                  value={formData.email} // **** formData.present_address?.email
                  onChange={handleChangeEmail}
                />
              </FormField>
              <FormField width={6}>
                <label style={labelStyle}>Line ID</label>
                <Input
                  name="line_id"
                  placeholder=""
                  value={formData.line_id}
                  onChange={handleChangeData}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={6} error={!!errors.relative_address_owner_name}>
                <label style={labelStyle}>
                  ชื่อผู้ติดต่อฉุกเฉิน{renderRequired("relative_address_owner_name")}
                </label>
                <Input
                  name="owner_name"
                  placeholder="กรุณาระบุชื่อ - นามสกุลผู้ติดต่อฉุกเฉิน"
                  value={formData.relative_address?.owner_name || ""}
                  onChange={handleChangeRelativeAddressData}
                  error={!!errors.relative_address_owner_name}
                />
              </FormField>
              <FormField width={6} error={!!errors.relative_address_tel_mobile}>
                <label style={labelStyle}>
                  เบอร์โทรผู้ติดต่อฉุกเฉิน{renderRequired("relative_address_tel_mobile")}
                </label>
                <Input
                  name="tel_mobile"
                  placeholder="กรุณาระบุหมายเลขโทรศัพท์ผู้ติดต่อฉุกเฉิน"
                  value={formData.relative_address?.tel_mobile || ""}
                  onChange={handleChangeRelativeAddressData}
                  error={!!errors.relative_address_tel_mobile}
                />
              </FormField>
              <FormField width={6}>
                <label style={labelStyle}>ความสัมพันธ์</label>
                <Dropdown
                  clearable={true}
                  fluid={true}
                  name="relative"
                  placeholder="กรุณาระบุความสัมพัมธ์ของผู้ติดต่อฉุกเฉิน"
                  onChange={handleChangeRelative}
                  options={props.masterOptions?.relative}
                  search={true}
                  selection={true}
                  style={{ width: "100%" }}
                  value={formData.relative_address?.relative}
                ></Dropdown>
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={16}>
                <div
                  style={{
                    width: "100%",
                    borderTop: "dotted 2px var(--primary-theme-color)",
                  }}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField>
                <label
                  style={{
                    ...labelStyle,
                    textDecoration: "underline",
                  }}
                >
                  ที่อยุ่ปัจจุบัน (สำหรับติดต่อ)
                </label>
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={6}>
                <label style={labelStyle}>บ้านเลขที่</label>
                <Input
                  name="no"
                  placeholder=""
                  value={formData.present_address?.no}
                  onChange={handleChangeAddressData}
                />
              </FormField>
              <FormField width={6}>
                <label style={labelStyle}>ชื่อหมู่บ้าน/อาคาร</label>
                <Input
                  name="name"
                  placeholder=""
                  value={formData.present_address?.name}
                  onChange={handleChangeAddressData}
                />
              </FormField>
              <FormField width={6} error={!!errors.type}>
                <label style={labelStyle}>ประเภทที่พักอาศัย{renderRequired("type")}</label>
                <Dropdown
                  name="type"
                  placeholder=""
                  selection
                  fluid
                  options={masterOptions?.homeType || []}
                  value={formData.present_address?.type || 0}
                  onChange={handleChangeAddressDropdown}
                  error={!!errors.type}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={6}>
                <label style={labelStyle}>หมู่ที่</label>
                <Input
                  name="town"
                  placeholder=""
                  value={formData.present_address?.town}
                  onChange={handleChangeAddressData}
                />
              </FormField>
              <FormField width={6}>
                <label style={labelStyle}>ซอย</label>
                <Input
                  name="street"
                  placeholder=""
                  value={formData.present_address?.street}
                  onChange={handleChangeAddressData}
                />
              </FormField>
              <FormField width={6}>
                <label style={labelStyle}>ถนน</label>
                <Input
                  name="road"
                  placeholder=""
                  value={formData.present_address?.road}
                  onChange={handleChangeAddressData}
                />
              </FormField>
            </FormGroup>

            <FormGroup>
              <FormField width={4} error={!!errors.province}>
                <label style={labelStyle}>จังหวัด{renderRequired("province")}</label>
                <Dropdown
                  name="province"
                  placeholder=""
                  selection
                  fluid
                  options={masterOptions?.province || []}
                  value={formData.present_address?.province || 0}
                  onChange={handleChangeAddressDropdown}
                  error={!!errors.province}
                />
              </FormField>
              <FormField width={4} error={!!errors.district}>
                <label style={labelStyle}>อำเภอ/เขต{renderRequired("district")}</label>
                <Dropdown
                  name="district"
                  placeholder=""
                  selection
                  fluid
                  options={districtOptions || []}
                  value={formData.present_address?.district || 0}
                  onChange={handleChangeAddressDropdown}
                  error={!!errors.district}
                />
              </FormField>
              <FormField width={4} error={!!errors.city}>
                <label style={labelStyle}>ตำบล/แขวง{renderRequired("city")}</label>
                <Dropdown
                  name="city"
                  placeholder=""
                  selection
                  fluid
                  options={cityOptions || []}
                  value={formData.present_address?.city || 0}
                  onChange={handleChangeAddressDropdown}
                  error={!!errors.city}
                />
              </FormField>
              <FormField width={4}>
                <label style={labelStyle}>รหัสไปรษณีย์</label>
                <Input
                  name="zipcode"
                  placeholder=""
                  readOnly
                  value={formData.present_address?.zipcode}
                  // onChange={handleChangeData}
                />
              </FormField>
            </FormGroup>
          </Form>

          {/* --- Action Buttons --- */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "1rem",
              marginTop: "2rem",
            }}
          >
            <Button
              onClick={handleClose}
              style={{
                backgroundColor: "#4a4a4a",
                color: "white",
                minWidth: "120px",
              }}
            >
              ปิดหน้าต่าง
            </Button>
            <ButtonLoadCheck
              setProp={props.setProp}
              onClick={handleSubmit}
              paramKey={isEditMode ? BTN_ACTS.UPDATE_CUSTOMER : BTN_ACTS.CREATE_CUSTOMER}
              buttonLoadCheck={
                props.buttonLoadCheck?.[
                  isEditMode ? BTN_ACTS.UPDATE_CUSTOMER : BTN_ACTS.CREATE_CUSTOMER
                ]
              }
              style={{
                backgroundColor: "var(--primary-theme-color)",
                color: "white",
                minWidth: "120px",
              }}
              title={submitButtonText}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

ModMeBillingCustomer.displayName = "ModMeBillingCustomer";

export default memo(ModMeBillingCustomer);

// เพิ่มการคัดกรอง Unicode
const sanitizeInput = (value: string) =>
  value
    // Zero Width Space, Zero Width Non-Joiner, Zero Width Joiner, BOM
    .replace(/[\u200B-\u200D\uFEFF\u2060\u3164]/g, "")
    // Word Joiner
    .replace(/\u2060/g, "")
    // Soft Hyphen
    .replace(/\u00AD/g, "")
    // Directional Marks
    .replace(/[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g, "")
    // Control characters (ยกเว้น \n, \r, \t)
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
